/**
 * Whose family portal you are looking at.
 *
 * A parent's pages ask for "my family", which the server resolves from who is
 * signed in. An administrator opening the same pages for a family is the one
 * exception: they name the family, and everything underneath becomes read-only,
 * because this is a window onto what that family sees rather than a place to
 * act on their behalf.
 *
 * Null — the ordinary case — means the signed-in parent's own family.
 */
import { createContext, useContext, type ReactNode } from 'react';

export interface ViewAsFamily {
  /** The family being viewed, or null when a parent is viewing their own. */
  familyId: string | null;
  /** True whenever someone other than the family is looking. */
  readOnly: boolean;
  /** Everything a link on these pages hangs off, e.g. /app/acme or /app/acme/view-as-family/123. */
  basePath: string;
}

const ViewAsFamilyContext = createContext<ViewAsFamily | null>(null);

export function ViewAsFamilyProvider({ value, children }: { value: ViewAsFamily; children: ReactNode }) {
  return <ViewAsFamilyContext.Provider value={value}>{children}</ViewAsFamilyContext.Provider>;
}

/**
 * Parent pages call this instead of reading route params: the same page is
 * mounted at two places, and only this says which one it is.
 */
export function useViewAsFamily(schoolSlug?: string | null): ViewAsFamily {
  const value = useContext(ViewAsFamilyContext);
  return value ?? { familyId: null, readOnly: false, basePath: `/app/${schoolSlug ?? ''}` };
}
